<template>
  <v-tooltip bottom v-if="shouldShow">
    <template v-slot:activator="{ on, attrs }">
      <span v-bind="attrs" v-on="on" class="tooltip-wrapper">
        <Button
          :label="computedLabel"
          :size="size"
          :tertiary="buttonType === 'tertiary'"
          :primary="buttonType === 'primary'"
          :icon="showIcon ? icon : null"
          :backgroundColor="computedBackgroundColor"
          @onClick="handleClick"
          :class="['issue-badge-button', buttonClass]"
        />
      </span>
    </template>
    <span>{{ tooltipText }}</span>
  </v-tooltip>
</template>

<script setup>
import { computed } from "vue";
import Button from "@components/buttons/Button.vue";
import { ACTION_CONFIG, ISSUE_BADGE_ACTIONS } from "./issueBadgeButtonConstants.js";

const props = defineProps({
  action: {
    type: String,
    default: null,
    validator: (value) =>
      value === null || ISSUE_BADGE_ACTIONS.includes(value),
  },
  label: {
    type: String,
    default: null,
  },
  tooltipText: {
    type: String,
    required: true,
  },
  backgroundColor: {
    type: String,
    default: null,
  },
  buttonType: {
    type: String,
    default: "tertiary",
    validator: (value) => ["tertiary", "primary"].includes(value),
  },
  size: {
    type: String,
    default: "small",
    validator: (value) => ["small", "normal", "medium", "large", "x-large", "hug"].includes(value),
  },
  icon: {
    type: String,
    default: null,
  },
  showIcon: {
    type: Boolean,
    default: false,
  },
  buttonClass: {
    type: String,
    default: "",
  },
  shouldShow: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["onClick"]);

const computedLabel = computed(() => {
  if (props.label) return props.label;
  if (props.action && ACTION_CONFIG[props.action]) {
    return ACTION_CONFIG[props.action].label;
  }
  return "";
});

const computedBackgroundColor = computed(() => {
  if (props.backgroundColor) return props.backgroundColor;
  if (props.action && ACTION_CONFIG[props.action]) {
    return ACTION_CONFIG[props.action].backgroundColor;
  }
  return "#7C3AED";
});

const handleClick = () => {
  emit("onClick");
};
</script>

<style scoped>
.tooltip-wrapper {
  display: inline-block;
  line-height: 0;
}

:deep(.issue-badge-button) {
  min-width: 95px;
  height: 32px !important;
  border-radius: 8px !important;
  border-width: 1px;
  padding: 8px 14px !important;
  gap: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  box-sizing: border-box;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  line-height: 20px !important;
  letter-spacing: 0% !important;
}

:deep(.issue-badge-button span) {
  margin: 0 !important;
  white-space: nowrap;
  vertical-align: middle;
  font-weight: 600 !important;
  font-size: 12px !important;
  line-height: 20px !important;
  letter-spacing: 0 !important;
}

:deep(.issue-badge-button img) {
  margin-right: 8px;
  vertical-align: middle;
}
</style>

